import { buildPayloadApiUrl, payloadFetch } from "./client";
import { mapEgdGamesResponse } from "src/utils/mapEgdGame";
import { mapEgdJackpotsResponse } from "src/utils/mapEgdJackpot";

/** @typedef {'new-and-popular' | 'progressive-jackpots' | 'cny'} EgdHubViewSlug */

/**
 * @typedef {Object} EgdHubCard
 * @property {import('src/utils/mapEgdGame').EgdGameCard} game
 * @property {import('src/utils/mapEgdJackpot').EgdJackpotMeter | null} [jackpot]
 */

/**
 * @typedef {Object} EgdHubViewResponse
 * @property {EgdHubViewSlug} view
 * @property {'en' | 'fr'} locale
 * @property {number} progressiveJackpotThreshold
 * @property {EgdHubCard[]} cards
 */

/**
 * Fetches featured EGD games from pg-payload-cms.
 * @param {'en' | 'fr'} locale
 * @returns {Promise<import('src/utils/mapEgdGame').EgdGameCard[]>}
 */
export async function fetchEgdGames(locale) {
  const url = buildPayloadApiUrl("/api/egd-games", {
    locale,
    limit: 200,
    depth: 0,
    sort: "_order",
  });

  const response = await payloadFetch(url);
  return mapEgdGamesResponse(response, locale);
}

/**
 * Fetches active progressive jackpot meters from pg-payload-cms.
 * @returns {Promise<import('src/utils/mapEgdJackpot').EgdJackpotMeter[]>}
 */
export async function fetchEgdJackpotMeters() {
  const url = buildPayloadApiUrl("/api/egd-jackpot-meters", {
    "where[active][equals]": true,
    limit: 500,
    depth: 1,
    sort: "game",
  });

  const response = await payloadFetch(url);
  return mapEgdJackpotsResponse(response);
}

/**
 * Fetches a view-ready EGD hub response from pg-payload-cms.
 *
 * @param {EgdHubViewSlug} view
 * @param {'en' | 'fr'} locale
 * @returns {Promise<EgdHubViewResponse>}
 */
export async function fetchEgdHubView(view, locale) {
  const url = buildPayloadApiUrl(`/api/egd/hub/${view}`, { locale });
  const response = await payloadFetch(url);

  return {
    view: response?.view ?? view,
    locale: response?.locale ?? locale,
    progressiveJackpotThreshold: response?.progressiveJackpotThreshold ?? 35000,
    cards: Array.isArray(response?.cards) ? response.cards : [],
  };
}
